import type { ReactNode } from 'react';
import { cx } from './util.js';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: 'start' | 'end';
  /** Sorting is the caller's: the header only reports the request and shows the state. */
  sortable?: boolean;
}

export interface DataTableProps<T> {
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: { key: string; direction: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}

/**
 * A table on desktop that collapses to labelled cards on a phone. The cards repeat each header
 * as a label, so no information is lost when the columns go away.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  onRowClick,
  empty,
}: DataTableProps<T>) {
  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;
  return (
    <table className="k-table" aria-label={label}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={cx('k-t-label-sm', column.align === 'end' && 'k-table__end')}
              aria-sort={
                sort?.key === column.key
                  ? sort.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined
              }
            >
              {column.sortable === true && onSort !== undefined ? (
                <button
                  type="button"
                  className="k-table__sort k-t-label-sm"
                  onClick={() => onSort(column.key)}
                >
                  {column.header}
                </button>
              ) : (
                column.header
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            className={cx(onRowClick !== undefined && 'k-table__row--click')}
            tabIndex={onRowClick === undefined ? undefined : 0}
            onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
            onKeyDown={
              onRowClick === undefined
                ? undefined
                : (event) => {
                    if (event.key === 'Enter') onRowClick(row);
                  }
            }
          >
            {columns.map((column) => (
              <td
                key={column.key}
                data-label={column.header}
                className={cx(column.align === 'end' && 'k-table__end')}
              >
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
